/**
 * **盤の器**（Issue #522 → #523）。テーマの層を**平面の紙**に描いて、その紙を盤（three の平面）に貼る。
 *
 * ## 平面の紙 1 枚に全部描いて、盤に貼る
 *
 * 2D canvas の変換はアフィンまでで**透視ができない**ので、「舞台のピクセルに遠近付きで描く」はできない。
 * 逆に**平面の座標の紙に描いて、その紙を盤のテクスチャにすれば遠近はカメラが付ける**。
 * 盤に印刷されているものは全部平面の上にあるので、これで**全層が 1 系統**になる。
 *
 * ```
 * ThreeStage（カメラ = view.shot）      ← ここだけがカメラを知る
 *   └ paper: 平面の矩形に貼るテクスチャ  ← 平面の座標の紙。テーマの層が全部描く
 * ```
 *
 * **貼り方は `matrix3d` から three に替えた**（Issue #523。ユーザー判断 2026-09-24）——
 * 厚み・浮き・影（オブジェクト）と演出も three で描くので、**投影をカメラ 1 つに一本化**して
 * 2 つの投影がずれる余地を無くす。紙の作り方は変わっていないので**テーマは 1 行も変わらない**。
 *
 * だから**テーマの描画関数はカメラを知らない**（平面の座標だけで書く）。実写と仮想の違いも
 * 「どの Field を渡すか」だけで、描く側は同じ。
 *
 * ## ここが持つもの / 持たないもの
 *
 * - 持つ（器）: 紙の範囲と解像度・`setTransform`・濃さ（`globalAlpha`）・投影と貼り付け
 * - 持たない: **層が何であるか**（`core/theme` の `FIELD_LAYERS`）・**層の絵**（`themes/<name>/drawField.js`）
 *
 * ## 素材と時刻の調達は呼ぶ側
 *
 * `<video>` もカードの画像も**ページが作って `scene` で渡す**（DOM に残る"素材"は外、"絵"は中）。
 * 時計も外で、ここは `scene.frameMs` を受け取って描き直すだけ＝**時計を持たない**（`purity.test.js`）。
 */
import { useMemo } from 'react';
import { FIELD_LAYERS, resolveLayers, resolveStyle, visibleLayers } from '../core/theme';
import { videoPlaneRect } from '../core/field';
import { placeBox, transformOf } from '../core/transform';
import ThreeStage from './ThreeStage';
import { withSplit } from './split';

/** 紙のビットマップの長辺（既定） */
export const DEFAULT_RESOLUTION = 2048;

const clamp01 = (v) => (Number.isFinite(v) ? Math.min(1, Math.max(0, v)) : 0);

/**
 * 紙の範囲（平面の座標）＝ **平面 ∪ 全ゾーンの外接矩形 ＋ 余白**。
 *
 * 同梱マットの `grave` は x 1106..1216 で**マット（幅 1080）の外**にある
 * （`client/synchro-client/src/core/field_gen/playmat_zones.py` に「意図的」とある）ので、
 * 平面ぴったりの紙だと墓地の区画だけ切れる。
 */
export function paperRect(scene) {
  const { w, h } = scene.field.plane;
  let x0 = 0; let y0 = 0; let x1 = w; let y1 = h;
  Object.values(scene.field.zones).forEach((r) => {
    x0 = Math.min(x0, r.x); y0 = Math.min(y0, r.y);
    x1 = Math.max(x1, r.x + r.w); y1 = Math.max(y1, r.y + r.h);
  });
  const pad = Math.max(8, scene.field.unit * 0.25);
  return { x: x0 - pad, y: y0 - pad, w: (x1 - x0) + pad * 2, h: (y1 - y0) + pad * 2 };
}

/** 紙のビットマップの倍率（長辺が `resolution` になる） */
export const paperScale = (paper, resolution = DEFAULT_RESOLUTION) => (
  Math.min(4, Math.max(0.25, (resolution > 0 ? resolution : DEFAULT_RESOLUTION) / Math.max(paper.w, paper.h)))
);

/** 平面の座標 → 紙のピクセルの変換を入れて、層を 1 つ描く（**層の絵の入口はここだけ**） */
function paintLayer(ctx, theme, name, scene, cfg, paper, k, alpha) {
  // 層が `save`/`restore` を取りこぼしても次の層に漏れないよう、毎回入れ直す
  ctx.setTransform(k, 0, 0, k, -paper.x * k, -paper.y * k);
  ctx.globalAlpha = alpha;
  theme.field[name](ctx, scene, cfg);
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.globalAlpha = 1;
}

/**
 * 見えている層を**全部 1 枚の紙に**描く。濃さは `globalAlpha` で重ねる。
 *
 * three に渡すときは**層ごとに 1 枚**（`fieldParts`）なのでこちらは通らないが、
 * 「層がどの順でどの濃さで呼ばれるか」を 1 か所で見るためにここに残してある。
 *
 * `setTransform` で「平面の座標 → 紙のピクセル」を入れてから層を呼ぶので、
 * **テーマは紙の原点ずれも倍率も知らない**（平面の座標のまま描く）。
 *
 * @returns 実際に描いた層の名前
 */
export function draw(ctx, theme, scene, config) {
  // **層には必ず object を渡す**（`config` を渡し忘れても層の中で落ちない）。
  // **`cfg.style` にテーマの見た目の値**が入る（Issue #523。宣言は `theme.schema`）
  const cfg = { ...(config ?? {}), style: resolveStyle(theme, config?.style) };
  const layers = resolveLayers(cfg.layers);
  const paper = paperRect(scene);
  const k = paperScale(paper, cfg.resolution);
  const names = visibleLayers(theme, scene, layers);
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.clearRect(0, 0, Math.ceil(paper.w * k), Math.ceil(paper.h * k));
  names.forEach((name) => paintLayer(ctx, theme, name, scene, cfg, paper, k, clamp01(layers[name])));
  return names;
}

/**
 * **その層が読むもの**（＝これが変わったら紙を描き直す）。
 *
 * `scene` のうち**フレームごとに変わるのは `frameMs` と `board` だけ**なので、それを読む層
 * （`needs` に `video` / `board` を宣言した層）だけが描き直す。他は 1 回描いたら据え置き
 * （毎フレーム 2048px の紙をテクスチャに上げ直すのは高い）。
 *
 * 裏を返すと **`needs` は前提であると同時に「フレームごとに変わるものをどれだけ読んでよいか」**で、
 * 宣言せずに読むと絵が止まって見える（`core/theme` の `FIELD_LAYERS` にも書いてある）。
 */
function layerDeps(layer, theme, scene, config) {
  // **書体が読めたら描き直す**（Issue #549。`scene.fontReady`）
  // **同梱の画像が読めたら描き直す**（Issue #565。`scene.imagesReady`。テーマの `images`）
  const deps = [theme, config, config?.style, scene.field, scene.cards, scene.players, scene.video, scene.fontReady, scene.imagesReady];
  if (layer.needs.includes('video')) deps.push(scene.frameMs);
  if (layer.needs.includes('board')) deps.push(scene.board);
  return deps;
}

/**
 * **盤に貼る紙**（three に渡す `parts`）。純関数。**層ごとに 1 枚**。
 *
 * 1 枚にまとめないのは**描き直す頻度が層ごとに違う**から —— 実写動画はフレームごとに変わるが、
 * マットとゾーンの枠は変わらない。1 枚だと動画のために毎フレーム全部を描き直してテクスチャを
 * 上げ直すことになる。重なり順は `FIELD_LAYERS` の宣言順（runtime が `renderOrder` に写す）。
 *
 * **描くのは runtime**（`draw` を渡すだけ）。ここで描いてテクスチャを上げる形にすると、
 * React の子（`ThreeStage`）の effect が親より先に走るので 1 フレーム古い絵が出る。
 * 描き直すかは `deps` の同一性で決まる。
 */
/**
 * **実写動画をそのまま GPU に渡すための置き方**（純関数。Issue #641）。
 *
 * 実写の層は「動画を平面に中心を合わせて置き、平面で切る」だけ（どのテーマの `drawLiveField` も同じ）なので、
 * 2D の紙に描いてから紙ごとテクスチャに上げ直す（毎フレーム 2048px）代わりに、**動画の要素をそのまま
 * テクスチャにして、見えている部分の矩形だけを貼る**。`texImage2D(video)` はブラウザが GPU の中で済ませるので軽い。
 *
 * @returns `{ rect（平面の座標。見えている部分）, uv: [u0, v0, u1, v1]（動画の中の割合。v は上から） }`。
 *   動画がまだ無い・見えている部分が無いときは null（呼ぶ側は紙に戻る）
 */
export function videoPlacement(scene) {
  const el = scene?.video?.el;
  const size = scene?.video?.size;
  const plane = scene?.field?.plane;
  if (!el || !(size?.w > 0) || !(size?.h > 0) || !(plane?.w > 0) || !(plane?.h > 0)) return null;
  // 要素の矩形（要素が元の映像の一部だけでも、置く場所は元の映像の座標で決まる。Issue #808）
  const r = videoPlaneRect(scene.video, plane);
  const x0 = Math.max(0, r.x); const y0 = Math.max(0, r.y);
  const x1 = Math.min(plane.w, r.x + r.w); const y1 = Math.min(plane.h, r.y + r.h);
  if (!(x1 > x0) || !(y1 > y0)) return null;
  return {
    rect: { x: x0, y: y0, w: x1 - x0, h: y1 - y0 },
    uv: [(x0 - r.x) / r.w, (y0 - r.y) / r.h, (x1 - r.x) / r.w, (y1 - r.y) / r.h],
  };
}

export function fieldParts(theme, scene, config = null) {
  const cfg = { ...(config ?? {}), style: resolveStyle(theme, config?.style) };
  const layers = resolveLayers(cfg.layers);
  const names = visibleLayers(theme, scene, layers);
  const paper = paperRect(scene);
  const k = paperScale(paper, cfg.resolution);
  const bitmap = { w: Math.max(1, Math.ceil(paper.w * k)), h: Math.max(1, Math.ceil(paper.h * k)) };
  return FIELD_LAYERS.filter((l) => names.includes(l.name)).map((layer) => {
    /*
     * **紙にもトランスフォームが付く**（Issue #523）。層ごとに 1 枚なので、
     * たとえば**実写動画だけをマットに対してずらす**（位置合わせ）ができる。
     * 絵は素の紙のまま描くので、動かしてもテクスチャは上げ直さない。
     */
    const t = placeBox(paper, transformOf(cfg.style, `field:${layer.name}`), 'board');
    /*
     * **実写動画は紙を通さない**（Issue #641）。回しが付いているときだけ紙に戻す
     * （見えている部分の矩形を紙の中心の周りで回すのは紙に任せたほうが確か）
     */
    const vp = layer.name === 'video' && !t.rot ? videoPlacement(scene) : null;
    if (vp) {
      // 紙の中心の周りの倍率・ずらしを、見えている部分の矩形に写す
      const pc = [paper.x + paper.w / 2, paper.y + paper.h / 2];
      const rc = [vp.rect.x + vp.rect.w / 2, vp.rect.y + vp.rect.h / 2];
      const c = [t.center[0] + (rc[0] - pc[0]) * t.scale, t.center[1] + (rc[1] - pc[1]) * t.scale];
      return {
        kind: 'paper',
        id: `field:${layer.name}`,
        rect: { x: c[0] - vp.rect.w / 2, y: c[1] - vp.rect.h / 2, w: vp.rect.w, h: vp.rect.h },
        scale: t.scale,
        rot: 0,
        bitmap: { w: 1, h: 1 },
        video: { el: scene.video.el, uv: vp.uv },
        opacity: clamp01(layers[layer.name]) * t.opacity,
        deps: layerDeps(layer, theme, scene, config),
      };
    }
    return {
      kind: 'paper',
      id: `field:${layer.name}`,
      rect: { x: t.center[0] - paper.w / 2, y: t.center[1] - paper.h / 2, w: paper.w, h: paper.h },
      scale: t.scale,
      rot: t.rot,
      bitmap,
      // 濃さは紙の `globalAlpha` ではなく**素材の不透明度**（層ごとに紙が分かれたので）
      opacity: clamp01(layers[layer.name]) * t.opacity,
      draw: (ctx) => {
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.clearRect(0, 0, bitmap.w, bitmap.h);
        paintLayer(ctx, theme, layer.name, scene, cfg, paper, k, 1);
      },
      // **deps は渡されたもので見る**（`cfg` は毎回作り直しているので、それで見ると全層が毎フレーム描き直しになる）
      deps: layerDeps(layer, theme, scene, config),
    };
  });
}

/**
 * @param theme `core/theme::toTheme()` を通したテーマ
 * @param scene そのフレームの素材 `{ field, frameMs, video: {el, size}, board, cards: {image, name} }`
 * @param view `stage/view.js::stageView` の結果（**カメラと舞台の大きさ**をここから取る）
 * @param config 設定 `{ layers, resolution, … }`（テーマ固有の設定もここに混ぜて渡る）
 */
/**
 * @param split 画面を切ってずらす（Issue #553。`stage/split.js::splitGeometry`）。演出の層と同じ値
 */
export default function FieldLayer({ theme, scene, view, config = null, split = null }) {
  const names = visibleLayers(theme, scene, resolveLayers(config?.layers));
  const parts = useMemo(() => fieldParts(theme, scene, config), [theme, scene, config]);

  return (
    <div className="de-layer" data-layer="field" data-layers={names.join(',')}>
      <ThreeStage canvas={view.canvas} shot={view.shot} plane={view.plane} parts={parts} post={withSplit(null, split)} testId="de-field-gl" scale={config?.renderScale ?? 1} />
    </div>
  );
}
